'use client';

/**
 * ObeyaProjectInfoCard — the composed "what is this project" read:
 * Actor identity + current ProjectConfig (client/type/dates/site/
 * budget) + current ProjectProgramme (room mix), joined server-side
 * into one payload (project_config_detail).
 *
 * design_freeze and committed_po_value render as "—" when null/0 —
 * that's the honest state (no signed Baseline, no real PO yet), never
 * a placeholder guess. Committed is a real computed sum over
 * PurchaseOrder.total_amount, not a typed-in figure.
 *
 * @see klyde_edge/klyde_intelligence/projects/handler.py — project_config_detail
 * @see klyde_edge/klyde_intelligence/projects/queries.py — get_project_config_detail
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useMemo } from 'react';
import { Building2 } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { cn } from '@/lib/utils/cn';
import { text, surfaceBase } from '@/lib/design/tokens';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';

interface ProjectConfigDetail {
  semantic_id: string;
  name: string | null;
  status: string | null;
  client: string | null;
  start_date: string | null;
  target_date: string | null;
  type: string | null;
  project_code: string | null;
  site_name: string | null;
  site_address: string | null;
  access: string | null;
  currency: string | null;
  approved_budget: number | null;
  margin: number | null;
  distinct_areas: number;
  total_rooms: number;
  po_count: number;
  committed_po_value: number | null;
  design_freeze: string | null;
}

function money(n: number | null | undefined): string {
  return n == null ? '—' : `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className={cn(text.muted, 'shrink-0')}>{label}</span>
      <span className={cn(text.title, 'truncate text-right')}>{value ?? '—'}</span>
    </div>
  );
}

export function ObeyaProjectInfoCard() {
  const { entityType, entityId, projectId: vsProjectId } = useViewportSelection();
  const storeProjectId = useGraphSelectionStore((s) => s.projectId);
  const projectId = entityType === 'project' ? entityId : (vsProjectId ?? storeProjectId);
  const { contentKey } = useCardIdentity();
  const strategy = useCapabilityStore((s) => s.strategyName('project_intelligence', 'config_detail'));

  const queryKey = ['strategy-data', strategy, { semantic_id: projectId ?? '' }] as const;
  const { data, isLoading } = useStrategyData<ProjectConfigDetail>(
    strategy, { semantic_id: projectId ?? '' }, !!projectId && !!strategy,
  );
  const d = data?.data;

  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_project_info',
    status: (d ? 'loaded' : projectId ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: projectId && strategy ? queryKey : null,
    text: d ? `${d.name} · ${d.client ?? 'no client'} · ${d.distinct_areas} areas / ${d.total_rooms} rooms` : 'Project Info',
    json: d ?? null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [contentKey, d, projectId, strategy]));

  if (!projectId) return <Shell>Select a project</Shell>;
  if (isLoading || !d) return <Shell>Loading…</Shell>;

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden" style={{ fontSize: surfaceBase.rail }}>
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
        <Building2 className="w-3.5 h-3.5 text-violet-400" />
        <span className={text.heading}>Project Info</span>
      </div>
      <div className="p-4 space-y-3">
        <div className="space-y-1.5">
          <div className={cn(text.muted, 'uppercase tracking-wide text-[10px]')}>Identity</div>
          <Row label="Project code" value={d.project_code} />
          <Row label="Client" value={d.client} />
          <Row label="Type" value={d.type} />
          <Row label="Currency" value={d.currency} />
        </div>

        <div className="space-y-1.5 pt-2 border-t border-border/50">
          <div className={cn(text.muted, 'uppercase tracking-wide text-[10px]')}>Site</div>
          <Row label="Property" value={d.site_name} />
          <Row label="Address" value={d.site_address} />
          <Row label="Access" value={d.access} />
        </div>

        <div className="space-y-1.5 pt-2 border-t border-border/50">
          <div className={cn(text.muted, 'uppercase tracking-wide text-[10px]')}>Key dates</div>
          <Row label="Start date" value={d.start_date} />
          <Row label="Design freeze" value={d.design_freeze} />
          <Row label="Target handover" value={d.target_date} />
        </div>

        <div className="space-y-1.5 pt-2 border-t border-border/50">
          <div className={cn(text.muted, 'uppercase tracking-wide text-[10px]')}>Rooms</div>
          <Row label="Distinct areas" value={d.distinct_areas} />
          <Row label="Total rooms" value={d.total_rooms} />
        </div>

        <div className="space-y-1.5 pt-2 border-t border-border/50">
          <div className={cn(text.muted, 'uppercase tracking-wide text-[10px]')}>Commercials</div>
          <Row label="Approved budget" value={money(d.approved_budget)} />
          <Row
            label="Committed (PO'd)"
            value={
              <span className={d.committed_po_value ? '' : 'text-muted-foreground'}>
                {money(d.committed_po_value)}
                {d.po_count === 0 && <span className={cn(text.muted, 'ml-1')}>(0 POs)</span>}
              </span>
            }
          />
        </div>
      </div>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <article className="rounded-md border border-border bg-card shadow-sm p-3" style={{ fontSize: surfaceBase.rail }}>
      <h3 className={cn(text.heading, 'mb-2')}>Project Info</h3>
      <div className={cn(text.muted, 'py-2')}>{children}</div>
    </article>
  );
}
